import { Component, EnvironmentInjector, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, Platform } from '@ionic/angular';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgLetDirective } from 'ng-let';
import { Capacitor } from '@capacitor/core';
import { Auth } from '@angular/fire/auth';

import { SocialSigninService, DevicePlatform } from '../social-signin/social-signin.service';
import { describeOtherProviders, isCredentialError } from '../social-signin/provider-mismatch';
import { UserService } from '../userService/user-service';
import { HttpClient } from '@angular/common/http';
import { ArmyBuilderConfig } from 'army-builder-shared';

/**
 * The standard login UI for every app in the monorepo. Styling lives in
 * global/styles/login.scss, scoped under .absLogin.
 */
@Component({
    standalone: true,
    imports: [CommonModule, IonicModule, NgLetDirective, ReactiveFormsModule, TranslateModule, RouterLink],
    selector: 'abs-login',
    template: `
        <div
            class="absLogin"
            *ngLet="{
                loginState: userService.login$ | async
            } as props"
        >
            <div class="error" *ngIf="props.loginState.error">
                {{ 'GLOBAL.ERRORS.' + props.loginState.error | translate }}
            </div>

            <div class="loggedIn" *ngIf="props.loginState.user; else loggedOut">
                <div class="buttons" *ngIf="showLogout">
                    <ion-button class="logout" (click)="logout()">
                        {{ 'GLOBAL.LOGIN.BUTTONS.LOGOUT' | translate }}
                        <ion-spinner name="crescent" *ngIf="props.loginState.loading"></ion-spinner>
                    </ion-button>
                </div>
            </div>
            <ng-template #loggedOut>
                <div class="loginButtons" [class.split]="splitLayout">
                    @if (enableMockLogin) {
                        <div class="buttons"><ion-button (click)="mockLogin()">Mock Login</ion-button></div>
                    }

                    <form *ngIf="showLogin" [formGroup]="loginForm" (ngSubmit)="loginWithCredentials()" class="loginForm gutter-collapse">
                        <div class="form-row">
                            <ion-label>{{ 'GLOBAL.LOGIN.EMAIL' | translate }}</ion-label>
                            <ion-input type="email" formControlName="email"></ion-input>
                        </div>
                        <div class="form-row">
                            <ion-label>{{ 'GLOBAL.LOGIN.PASSWORD' | translate }}</ion-label>
                            <ion-input type="password" formControlName="password"></ion-input>
                        </div>

                        <p class="error" *ngIf="error">{{ 'GLOBAL.ERRORS.' + error | translate }}</p>

                        <p class="error" *ngIf="providerHint">
                            {{ 'GLOBAL.LOGIN.PROVIDER_MISMATCH.LOGIN' | translate: { providers: providerHint } }}
                        </p>

                        <div class="buttons">
                            <ion-button type="submit" [disabled]="loginForm.invalid">
                                {{ 'GLOBAL.LOGIN.BUTTONS.LOGIN' | translate }}
                                <ion-spinner name="crescent" *ngIf="props.loginState.loading"></ion-spinner>
                            </ion-button>
                        </div>

                        <p class="authPrompt resetPrompt">
                            <a (click)="goToPasswordReset()">{{ 'GLOBAL.LOGIN.RESET_PASSWORD.PROMPT' | translate }}</a>
                        </p>

                        <div class="error" *ngIf="linkingCollision">
                            <ng-container *ngIf="linkingCollision.customData?.email as collisionEmail; else genericLinkingMessage">
                                {{ 'GLOBAL.LOGIN.LINKING_COLLISION.WITH_EMAIL' | translate: { email: collisionEmail } }}
                            </ng-container>
                            <ng-template #genericLinkingMessage>
                                {{ 'GLOBAL.LOGIN.LINKING_COLLISION.GENERIC' | translate }}
                            </ng-template>
                        </div>
                    </form>

                    <p class="authPrompt registerPrompt" *ngIf="showRegister">
                        {{ 'GLOBAL.LOGIN.REGISTER.PROMPT' | translate }}
                        <a [routerLink]="'/register'">{{ 'GLOBAL.LOGIN.BUTTONS.REGISTER' | translate }}</a>
                    </p>

                    <div class="divider">
                        <span>{{ 'GLOBAL.LOGIN.SOCIAL_DIVIDER' | translate }}</span>
                    </div>

                    <div class="buttons vertical social gutter-collapse">
                        <ion-button type="submit" *ngIf="isIOS" class="apple" (click)="appleLogin()">
                            <ion-icon name="logo-apple" slot="start"></ion-icon>
                            Apple <ion-spinner name="crescent" *ngIf="props.loginState.loading"></ion-spinner>
                        </ion-button>
                        <ion-button type="submit" class="google" color="danger" (click)="googleLogin()">
                            <ion-icon name="logo-google" slot="start"></ion-icon>
                            Google <ion-spinner name="crescent" *ngIf="props.loginState.loading"></ion-spinner>
                        </ion-button>
                        <ion-button type="submit" *ngIf="!isIOS" class="apple" (click)="appleLogin()">
                            <ion-icon name="logo-apple" slot="start"></ion-icon>
                            Apple <ion-spinner name="crescent" *ngIf="props.loginState.loading"></ion-spinner>
                        </ion-button>
                    </div>
                </div>
            </ng-template>
        </div>
    `
})
export class LoginComponent {
    @Input()
    showLogout: boolean = true;

    @Input()
    showLogin: boolean = true;

    @Input()
    showRegister: boolean = true;

    // Renders the email/password form and social buttons side by side (form on the left,
    // social on the right) instead of stacked. Used only for the desktop home-page embed —
    // the standalone /login page keeps the stacked layout at all widths.
    @Input()
    splitLayout: boolean = false;

    loginForm = this.fb.group({
        email: this.fb.control('', [Validators.required, Validators.email]),
        password: this.fb.control('', [Validators.required])
    });

    // Set when signInWithEmail() collides with an account that was originally created via a
    // different provider (auth/account-exists-with-different-credential). Holds the raw Firebase
    // error, including the retained `credential` from the interrupted attempt, so it can be linked
    // onto the account once the user proves ownership by signing in via their original provider.
    linkingCollision: any = null;

    error: string | null = null;

    // The provider name(s) to point the user at when the address they typed turns out to belong to
    // a social-only account — shown instead of `error`, because "incorrect email or password" is
    // actively misleading for an account that has no password. Null whenever we learned nothing,
    // which includes every way the lookup can fail.
    providerHint: string = null;

    // Platform.is() is synchronous and user-agent based, so it's stable for the page lifetime.
    // Pre-calculated once in the constructor (rather than called from the template) per the
    // "never call plain methods from templates" rule.
    readonly isIOS: boolean;

    enableMockLogin = this.appConfig.enableMockLogin;

    constructor(
        public userService: UserService,
        private fb: UntypedFormBuilder,
        private socialSignin: SocialSigninService,
        private auth: Auth,
        private router: Router,
        public platform: Platform,
        private appConfig: ArmyBuilderConfig
    ) {
        this.isIOS = this.platform.is('ios');
    }

    logout() {
        this.userService.logout();
    }

    // Navigates imperatively rather than binding [queryParams] to a getter: template-bound
    // getters are re-evaluated every change-detection cycle and would allocate a fresh object
    // each time (see the template performance rules in .github/copilot-instructions.md).
    goToPasswordReset() {
        const email = this.loginForm.value.email;
        this.router.navigate(['/reset-password'], { queryParams: email ? { email } : {} });
    }

    async googleLogin() {
        const platform: DevicePlatform = this.getPlatform();
        await this.socialSignin.googleLogin(platform);
        await this.completeLinkingIfPending();
    }

    private getPlatform() {
        let platform: DevicePlatform;
        // Check if running as a native Capacitor app first
        if (Capacitor.isNativePlatform()) {
            if (this.platform.is('android')) {
                platform = 'android';
            } else if (this.platform.is('ios')) {
                platform = 'ios';
            } else {
                platform = 'web';
            }
        } else {
            // Running in browser (including mobile web)
            platform = 'web';
        }
        return platform;
    }

    async appleLogin() {
        const platform: DevicePlatform = this.getPlatform();
        await this.socialSignin.appleLogin(platform);
        await this.completeLinkingIfPending();
    }

    async facebookLogin() {
        const platform: DevicePlatform = this.getPlatform();
        await this.socialSignin.facebookLogin(platform);
        await this.completeLinkingIfPending();
    }

    async mockLogin() {
        try {
            const credential = await this.socialSignin.mockLogin();
            this.socialSignin.handleCredentialResponse(await credential.token);
        } catch (err: any) {
            this.error = err.code || 'UNKNOWN';
        }
    }

    async loginWithCredentials() {
        if (!this.loginForm.valid) {
            return;
        }

        this.linkingCollision = null;
        this.error = null;
        this.providerHint = null;
        const { email, password } = this.loginForm.value;

        try {
            const credential = await this.socialSignin.signInWithEmail(email, password);
            this.socialSignin.handleCredentialResponse(await credential.user.getIdToken());
        } catch (err: any) {
            if (err.code === 'auth/account-exists-with-different-credential') {
                this.linkingCollision = err;
                return;
            }

            // Set first, so it stands as the message unless the lookup below positively improves
            // on it. Everything after this point is advisory and runs only once the sign-in has
            // already failed — it can never delay or block a successful login.
            this.error = err.code || 'UNKNOWN';

            if (isCredentialError(err.code)) {
                const hint = describeOtherProviders(await this.socialSignin.getSignInProviders(email));
                if (hint) {
                    this.providerHint = hint;
                    this.error = null;
                }
            }
        }
    }

    // Completes an in-flight account-linking collision once the user has re-authenticated via
    // their original provider (googleLogin/appleLogin/facebookLogin above). Firebase's
    // linkWithCredential requires the caller to already be signed in as the target account, so
    // this can only ever run after that re-authentication succeeds -- it is never called from
    // loginWithCredentials() directly. The email check below is a defense-in-depth guard against
    // linking the retained credential onto the wrong account if the user picks a provider other
    // than the one the collision was actually reported against.
    private async completeLinkingIfPending() {
        if (!this.linkingCollision) {
            return;
        }

        const currentUser = this.auth.currentUser;
        if (!currentUser || currentUser.email !== this.linkingCollision.customData?.email) {
            return;
        }

        try {
            await this.socialSignin.linkCredential(currentUser, this.linkingCollision.credential);
            this.linkingCollision = null;
        } catch (error) {
            console.error('Error linking credential', error);
        }
    }
}
